import { useState, useMemo, useEffect, useRef, useCallback, Fragment } from 'react';
import { Plane, Car, Leaf, Info } from 'lucide-react';
import * as d3 from 'd3';
import * as XLSX from 'xlsx';
import ScrollIndicator from './ScrollIndicator';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Skeleton } from './ui/skeleton';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

// Reusable "Source ?" badge with hover tooltip
function InfoBadge({ label = 'Source', tooltip }) {
  return (
    <span className="relative inline-flex items-center gap-1 group select-none cursor-default">
      <span className="text-[11px] text-gray-500 underline decoration-dotted">{label}</span>
      <Info className="w-3.5 h-3.5 text-gray-500" />
      <span
        className="pointer-events-none absolute left-0 top-full mt-1 w-max max-w-[280px] rounded border bg-white/95 text-[10px] text-gray-800 shadow-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
        style={{ backdropFilter: 'blur(2px)' }}
      >
        {tooltip}
      </span>
    </span>
  );
}

// === GWP Benchmarking v4: constants & helpers =====================

function createTextMeasurer(font = '11px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial') {
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const ctx = canvas ? canvas.getContext('2d') : null;
  return (txt) => {
    const t = String(txt ?? '');
    if (!ctx) return 14 + Math.max(2, t.length) * 5.6;
    ctx.font = font;
    return Math.ceil(ctx.measureText(t).width) + 12;
  };
}

const GWP_TARGETS = [
  { id: 'dk-2029', label: 'DK 2029', short: 'DK 2029', target: 6.9, priority: 1 },
  { id: 'dk-2027', label: 'DK 2027', short: 'DK 2027', target: 7.7, priority: 1 },
  { id: 'dk-2025', label: 'DK 2025', short: 'DK 2025', target: 8.6, priority: 1 },
  { id: 'fr-office', label: 'France', short: 'France*', target: 14.8, priority: 2 },
  { id: 'se', label: 'Sweden', short: 'Sweden', target: 14, priority: 2 },
  { id: 'no', label: 'Norway', short: 'Norway', target: 15, priority: 2 },
  { id: 'nl', label: 'Netherlands', short: 'NL', target: 16, priority: 2 },
  { id: 'ca', label: 'Canada', short: 'Canada', target: 19, priority: 3 },
  { id: 'au', label: 'Australia', short: 'Australia', target: 29, priority: 3 },
];

const LABEL_ANCHOR_OVERRIDES = {
  'dk-2029': 'right-edge',
  'dk-2025': 'left-edge',
};

const getGwpThreshold = () => 15;

const ELECTRICITY_METADATA_URL = 'https://ourworldindata.org/grapher/per-capita-electricity-source-stacked.metadata.json?v=1&csvType=full&useColumnShortNames=false';
const ELECTRICITY_DATA_URL = 'https://ourworldindata.org/grapher/per-capita-electricity-source-stacked.csv?download-format=tab';

const ELECTRICITY_SOURCE_DEFS = [
  {
    key: 'Coal electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Coal',
    label: 'Coal',
    color: '#78350f',
    group: 'Fossil',
  },
  {
    key: 'Gas electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Gas',
    label: 'Gas',
    color: '#b45309',
    group: 'Fossil',
  },
  {
    key: 'Oil electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Oil',
    label: 'Oil',
    color: '#4c1d95',
    group: 'Fossil',
  },
  {
    key: 'Nuclear electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Nuclear',
    label: 'Nuclear',
    color: '#0f766e',
    group: 'Firm low-carbon',
  },
  {
    key: 'Hydro electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Hydropower',
    label: 'Hydro',
    color: '#0ea5e9',
    group: 'Renewable',
  },
  {
    key: 'Wind electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Wind',
    label: 'Wind',
    color: '#22c55e',
    group: 'Renewable',
  },
  {
    key: 'Solar electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Solar',
    label: 'Solar',
    color: '#f59e0b',
    group: 'Renewable',
  },
  {
    key: 'Bioenergy electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Bioenergy',
    label: 'Bioenergy',
    color: '#a855f7',
    group: 'Renewable',
  },
  {
    key: 'Other renewable electricity excluding bioenergy per capita - kWh (adapted for visualization of chart per-capita-electricity-source-stacked)',
    dataKey: 'Other renewables',
    label: 'Others',
    color: '#ec4899',
    group: 'Renewable',
    infoLabel: 'Other renewables',
  },
];

const FOSSIL_METADATA_URL = 'https://ourworldindata.org/grapher/per-capita-electricity-fossil-nuclear-renewables.metadata.json?v=1&csvType=full&useColumnShortNames=false';
const FOSSIL_DATA_URL = 'https://ourworldindata.org/grapher/per-capita-electricity-fossil-nuclear-renewables.csv?download-format=tab';

const FOSSIL_SOURCE_DEFS = [
  {
    key: 'Fossil fuel electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-fossil-nuclear-renewables)',
    dataKey: 'Fossil fuels',
    label: 'Fossil fuels',
    color: '#b91c1c',
  },
  {
    key: 'Nuclear electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-fossil-nuclear-renewables)',
    dataKey: 'Nuclear',
    label: 'Nuclear',
    color: '#2563eb',
  },
  {
    key: 'Renewable electricity per capita - kWh (adapted for visualization of chart per-capita-electricity-fossil-nuclear-renewables)',
    dataKey: 'Renewables',
    label: 'Renewables',
    color: '#2f8c64',
  },
];

const formatLastUpdatedLabel = (date) => {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return `Data updated on ${parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

const getMetadataLastUpdatedLabel = (metadata, sourceDefs) => {
  if (!metadata?.columns) return null;
  const columns = metadata.columns;
  for (const def of sourceDefs) {
    const column = columns[def.key];
    if (column?.lastUpdated) {
      return formatLastUpdatedLabel(column.lastUpdated);
    }
  }
  for (const column of Object.values(columns)) {
    if (column?.lastUpdated) {
      return formatLastUpdatedLabel(column.lastUpdated);
    }
  }
  return null;
};

const getSeriesRecordForYear = (series, targetYear) => {
  if (!series?.length) return null;
  if (targetYear != null) {
    const exact = series.find((entry) => entry.year === targetYear);
    if (exact) return exact;
  }
  return series[series.length - 1] || null;
};

const SUMMARY_BAR_COLORS = {
  fossil: '#b91c1c',
  firm: '#2563eb',
  renewables: '#2f8c64',
};

const SUMMARY_BAR_LABELS = {
  fossil: 'Fossil',
  firm: 'Firm',
  renewables: 'Renewables',
};


let electricityMixCache = null;
let electricityMixPromise = null;

const buildElectricityMaps = (csvText) => {
  const rows = d3.csvParse(csvText);
  const entityMap = new Map();

  rows.forEach((row) => {
    const entityName = (row.Entity || row.entity || '').trim();
    if (!entityName) return;
    const year = Number(row.Year ?? row.year);
    if (!Number.isFinite(year)) return;
    const code = (row.Code || row.code || '').trim();

    if (!entityMap.has(entityName)) {
      entityMap.set(entityName, {
        name: entityName,
        code,
        codeLower: code.toLowerCase(),
        nameLower: entityName.toLowerCase(),
        series: [],
      });
    }

    const entry = entityMap.get(entityName);
    if (!entry.code && code) {
      entry.code = code;
      entry.codeLower = code.toLowerCase();
    }

    const values = {};
    let total = 0;
    ELECTRICITY_SOURCE_DEFS.forEach((source) => {
      const raw = parseFloat(row[source.key] ?? row[source.dataKey]);
      const numeric = Number.isFinite(raw) ? raw : 0;
      values[source.key] = numeric;
      total += numeric;
    });

    entry.series.push({ year, values, total });
  });

  entityMap.forEach((entry) => {
    entry.series.sort((a, b) => a.year - b.year);
  });

  const entitiesList = Array.from(entityMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  return { entityMap, entitiesList };
};

const buildFossilMap = (csvText) => {
  const rows = d3.csvParse(csvText);
  const fossilMap = new Map();

  rows.forEach((row) => {
    const entityName = (row.Entity || row.entity || '').trim();
    if (!entityName) return;
    const year = Number(row.Year ?? row.year);
    if (!Number.isFinite(year)) return;

    if (!fossilMap.has(entityName)) {
      fossilMap.set(entityName, {
        name: entityName,
        series: [],
      });
    }

    const entry = fossilMap.get(entityName);
    const values = {};
    FOSSIL_SOURCE_DEFS.forEach((source) => {
      const raw = parseFloat(row[source.key] ?? row[source.dataKey]);
      const numeric = Number.isFinite(raw) ? raw : 0;
      values[source.key] = numeric;
    });

    entry.series.push({ year, values });
  });

  fossilMap.forEach((entry) => {
    entry.series.sort((a, b) => a.year - b.year);
  });

  return fossilMap;
};

const prepareElectricityMixData = async () => {
  if (electricityMixCache) return electricityMixCache;
  if (electricityMixPromise) return electricityMixPromise;

  electricityMixPromise = (async () => {
    const [metaResponse, dataResponse, fossilMetaResponse, fossilDataResponse] = await Promise.all([
      fetch(ELECTRICITY_METADATA_URL),
      fetch(ELECTRICITY_DATA_URL),
      fetch(FOSSIL_METADATA_URL),
      fetch(FOSSIL_DATA_URL),
    ]);

    if (!metaResponse.ok) throw new Error(`Metadata request failed (${metaResponse.status})`);
    if (!dataResponse.ok) throw new Error(`Dataset request failed (${dataResponse.status})`);
    if (!fossilMetaResponse.ok) throw new Error(`Fossil dataset metadata failed (${fossilMetaResponse.status})`);
    if (!fossilDataResponse.ok) throw new Error(`Fossil dataset request failed (${fossilDataResponse.status})`);

    const [metaJson, csvText, fossilMetaJson, fossilCsvText] = await Promise.all([
      metaResponse.json(),
      dataResponse.text(),
      fossilMetaResponse.json(),
      fossilDataResponse.text(),
    ]);

    const { entityMap, entitiesList } = buildElectricityMaps(csvText);
    const fossilEntityMap = buildFossilMap(fossilCsvText);

    return {
      entityMap,
      entitiesList,
      metadata: metaJson || null,
      fossilEntityMap,
      fossilMetadata: fossilMetaJson || null,
    };
  })()
    .then((data) => {
      electricityMixCache = data;
      return data;
    })
    .catch((err) => {
      electricityMixCache = null;
      throw err;
    })
    .finally(() => {
      electricityMixPromise = null;
    });

  return electricityMixPromise;
};

function placePills({
  xScale,
  items,
  buildingGWP,
  threshold,
  getPillW,
  minGap = 8,
  lanes = [
    { name: 'country-l1', yOffset: 65, pills: [] },
    { name: 'country-l2', yOffset: 95, pills: [] },
  ],
}) {
  const innerL = xScale.range()[0];
  const innerR = xScale.range()[1];
  const buildingX = xScale(buildingGWP);
  const thresholdX = xScale(threshold);

  const laneStates = lanes.map((lane) => ({ ...lane, pills: [] }));

  const sorted = [...items].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    const distA = Math.abs(a.target - buildingGWP);
    const distB = Math.abs(b.target - buildingGWP);
    if (distA !== distB) return distA - distB;
    return a.target - b.target;
  });

  const clampCenter = (center, w) => {
    const minCenter = innerL + w / 2;
    const maxCenter = innerR - w / 2;
    return Math.max(minCenter, Math.min(maxCenter, center));
  };

  const computeLaneFit = (lane, idealX, w) => {
    const minCenter = innerL + w / 2;
    const maxCenter = innerR - w / 2;
    const clampedIdeal = Math.min(Math.max(idealX, minCenter), maxCenter);
    const pills = lane.pills;

    if (!pills.length) {
      return {
        x: clampedIdeal,
        overlap: false,
        distance: Math.abs(clampedIdeal - idealX),
        gapPenalty: 0,
      };
    }

    const segments = [];
    let cursor = minCenter;
    for (const pill of pills) {
      const gap = (pill.w / 2) + (w / 2) + minGap;
      const end = pill.x - gap;
      if (end > cursor) segments.push([cursor, end]);
      cursor = Math.max(cursor, pill.x + gap);
    }
    if (cursor < maxCenter) segments.push([cursor, maxCenter]);

    if (segments.length) {
      let bestSegment = segments[0];
      let bestDist = Number.POSITIVE_INFINITY;
      for (const seg of segments) {
        const [startSeg, endSeg] = seg;
        const dist = clampedIdeal < startSeg ? startSeg - clampedIdeal : clampedIdeal > endSeg ? clampedIdeal - endSeg : 0;
        if (dist < bestDist - 1e-6) {
          bestDist = dist;
          bestSegment = seg;
        }
      }
      const [startSeg, endSeg] = bestSegment;
      const x = Math.min(Math.max(clampedIdeal, startSeg), endSeg);
      const room = endSeg - startSeg;
      return {
        x,
        overlap: false,
        distance: Math.abs(x - idealX),
        gapPenalty: room < w ? (w - room) : 0,
      };
    }

    const minClamp = innerL + w / 2;
    const maxClamp = innerR - w / 2;
    const candidates = new Set([clampedIdeal]);
    for (const pill of pills) {
      const gap = (pill.w / 2) + (w / 2) + minGap;
      candidates.add(Math.max(minClamp, Math.min(maxClamp, pill.x + gap)));
      candidates.add(Math.max(minClamp, Math.min(maxClamp, pill.x - gap)));
    }

    let best = null;
    for (const cand of candidates) {
      let penalty = 0;
      for (const pill of pills) {
        const gap = (pill.w / 2) + (w / 2) + minGap;
        const diff = Math.abs(pill.x - cand);
        if (diff < gap) penalty += gap - diff;
      }
      const dist = Math.abs(cand - idealX);
      penalty += dist * 0.25;
      if (!best || penalty < best.penalty) {
        best = { x: cand, penalty, distance: dist };
      }
    }

    return {
      x: best.x,
      overlap: true,
      distance: best.distance,
      gapPenalty: best.penalty,
    };
  };

  const insertPillSorted = (pills, pill) => {
    let idx = pills.findIndex((p) => p.x > pill.x);
    if (idx === -1) idx = pills.length;
    pills.splice(idx, 0, pill);
  };

  const placed = [];

  sorted.forEach((item) => {
    const idealX = xScale(item.target);
    const w = Math.max(40, Math.min(120, getPillW(item)));
    const preferredLane = Math.min(Math.max((item.priority || 1) - 1, 0), laneStates.length - 1);

    let bestPlacement = null;
    laneStates.forEach((lane, li) => {
      const fit = computeLaneFit(lane, idealX, w);
      let penalty = fit.distance;
      penalty += lane.pills.length * 12;
      penalty += Math.abs(li - preferredLane) * 18;

      if (fit.overlap) penalty += 400 + fit.gapPenalty * 8;
      else penalty += fit.gapPenalty * 2;

      const distToBuilding = Math.abs(fit.x - buildingX);
      if (distToBuilding < w * 0.55) {
        penalty += (w * 0.55 - distToBuilding) * 3;
      }
      const distToThreshold = Math.abs(fit.x - thresholdX);
      if (distToThreshold < w * 0.5) {
        penalty += (w * 0.5 - distToThreshold) * 2;
      }

      if (!bestPlacement || penalty < bestPlacement.penalty) {
        bestPlacement = { laneIndex: li, x: fit.x, penalty };
      }
    });

    if (!bestPlacement) {
      bestPlacement = { laneIndex: preferredLane, x: clampCenter(idealX, w), penalty: Number.POSITIVE_INFINITY };
    }

    const lane = laneStates[bestPlacement.laneIndex];
    const pill = { id: item.id, x: bestPlacement.x, w, target: item.target };
    insertPillSorted(lane.pills, pill);

    placed.push({
      ...item,
      x: bestPlacement.x,
      w,
      lane: bestPlacement.laneIndex,
      yOffset: lane.yOffset,
      anchorX: idealX,
    });
  });

  return { placed, buildingX, thresholdX };
}

function makeSmartLabeler(items, xScale, pillW) {
  return (target, viewportW) => {
    const x = xScale(target.target);
    const neighbors = items.filter(i => Math.abs(xScale(i.target) - x) < pillW * 0.85).length;

    if (viewportW < 520 || neighbors >= 5) return target.short;
    if (neighbors >= 3 || viewportW < 680) return target.short;
    return target.label;
  };
}

function resolveCenteredLabelVisibility(
  placed,
  xScale,
  buildingGWP,
  gutter = 8,
  measureTextWidth,
  getPrimaryText,
  anchorOverrides = {}
) {
  const measure = measureTextWidth || ((txt) => 14 + Math.max(2, (txt || '').length) * 5.6);

  const innerL = xScale.range()[0];
  const innerR = xScale.range()[1];
  const EPS = 2.0;

  const chooseLoser = (a, b) => {
    const priA = a.priority ?? Number.POSITIVE_INFINITY;
    const priB = b.priority ?? Number.POSITIVE_INFINITY;
    if (priA !== priB) return priA > priB ? a : b;

    const distA = Math.abs((a.target ?? 0) - buildingGWP);
    const distB = Math.abs((b.target ?? 0) - buildingGWP);
    if (distA !== distB) return distA > distB ? a : b;

    if ((a.target ?? 0) !== (b.target ?? 0)) {
      return (a.target ?? 0) > (b.target ?? 0) ? a : b;
    }
    return (a.id || a.label || '') > (b.id || b.label || '') ? a : b;
  };

  const byLane = new Map();
  placed.forEach((p) => {
    const arr = byLane.get(p.lane) || [];
    arr.push(p);
    byLane.set(p.lane, arr);
  });
  byLane.forEach((arr) => arr.sort((a, b) => (a.anchorX ?? a.x) - (b.anchorX ?? b.x)));

  const clampLeft = (left, w) => Math.max(innerL, Math.min(innerR - w, left));

  const layoutMap = new Map();

  byLane.forEach((arr) => {
    const visibleStack = [];

    for (const item of arr) {
      const anchor = item.anchorX ?? item.x;
      const mode = anchorOverrides[item.id] || 'center';

      const primaryText = getPrimaryText ? getPrimaryText(item) : (item.short || item.label);
      const wPrimary = measure(primaryText);

      const shortText = item.short || item.label;
      const wShort = measure(shortText);

      const positionFor = (w) => {
        if (mode === 'left-edge') {
          const left = clampLeft(anchor, w);
          return { left, right: left + w, w, alignClass: 'text-left' };
        }
        if (mode === 'right-edge') {
          const left = clampLeft(anchor - w, w);
          return { left, right: left + w, w, alignClass: 'text-right' };
        }
        const left = clampLeft(anchor - w / 2, w);
        return { left, right: left + w, w, alignClass: 'text-center' };
      };

      let choice = { useShort: false, text: primaryText, ...positionFor(wPrimary) };

      const last = visibleStack[visibleStack.length - 1];
      const overlapsPrimary = last && (choice.left < (last.right + gutter - EPS));

      if (overlapsPrimary) {
        const tryShort = wShort < wPrimary ? { useShort: true, text: shortText, ...positionFor(wShort) } : null;
        const stillOverlaps = tryShort && last && (tryShort.left < (last.right + gutter - EPS));

        if (tryShort && !stillOverlaps) {
          choice = tryShort;
        } else {
          const loser = chooseLoser(last.item, item);
          if (loser.id === item.id) {
            layoutMap.set(item.id, { hidden: true });
            continue;
          } else {
            const rec = layoutMap.get(last.item.id);
            if (rec) rec.hidden = true;
            visibleStack.pop();
            if (tryShort && tryShort.w < choice.w) choice = tryShort;
          }
        }
      }

      layoutMap.set(item.id, {
        hidden: false,
        boxLeft: choice.left,
        w: choice.w,
        useShort: choice.useShort,
        text: choice.text,
        alignClass: choice.alignClass,
      });
      visibleStack.push({ left: choice.left, right: choice.left + choice.w, item });
    }
  });

  placed.forEach((p) => {
    if (!layoutMap.has(p.id)) {
      const mode = anchorOverrides[p.id] || 'center';
      const anchor = p.anchorX ?? p.x;
      const text = getPrimaryText ? getPrimaryText(p) : (p.short || p.label);
      const w = measure(text);
      let left;
      if (mode === 'left-edge') left = clampLeft(anchor, w);
      else if (mode === 'right-edge') left = clampLeft(anchor - w, w);
      else left = clampLeft(anchor - w / 2, w);
      layoutMap.set(p.id, {
        hidden: false,
        boxLeft: left,
        w,
        useShort: false,
        text,
        alignClass: mode === 'left-edge' ? 'text-left' : mode === 'right-edge' ? 'text-right' : 'text-center',
      });
    }
  });

  return layoutMap;
}

function GWPBenchmark({ buildingGWP, targets = GWP_TARGETS }) {
  const height = 150;
  const pad = { top: 6, right: 20, bottom: 90, left: 20 };
  const railY = 55;
  const railH = 21;
  const measureTextWidth = useMemo(() => createTextMeasurer(), []);

  const [width, setWidth] = useState(860);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new ResizeObserver((ents) => {
      for (const e of ents) setWidth(Math.max(420, e.contentRect.width));
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const containerStyle = { overflow: 'visible' };

  const innerW = width - pad.left - pad.right;
  const threshold = getGwpThreshold();

  const maxVal = Math.max(threshold, buildingGWP, ...targets.map(t => t.target));
  const domainMax = Math.ceil(maxVal * 1.1);
  const x = d3.scaleLinear().domain([0, domainMax]).range([0, innerW]).nice();

  const smartLabel = makeSmartLabeler(targets, x, 96);

  const getPrimaryText = useCallback(
    (t) => smartLabel(t, width) || t.short || t.label || '',
    [smartLabel, width]
  );

  const getPillW = useCallback(
    (t) => measureTextWidth(getPrimaryText(t)),
    [measureTextWidth, getPrimaryText]
  );

  const { placed, thresholdX } = placePills({
    xScale: x,
    items: targets,
    buildingGWP,
    threshold,
    getPillW,
  });
  const layoutMap = resolveCenteredLabelVisibility(
    placed,
    x,
    buildingGWP,
    6,
    measureTextWidth,
    getPrimaryText,
    LABEL_ANCHOR_OVERRIDES
  );

  return (
    <div ref={ref} className="relative w-full overflow-visible">
      <svg width="100%" height={height} className="overflow-visible" style={containerStyle} overflow="visible">
        <g transform={`translate(${pad.left},${pad.top})`} style={containerStyle}>
          <defs>
            <linearGradient id="gwpRailGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#34d399"/>
              <stop offset={`${Math.min(100, (x(threshold) / innerW) * 100)}%`} stopColor="#fde047"/>
              <stop offset="100%" stopColor="#f87171"/>
            </linearGradient>
          </defs>

          {(() => {
            const ticks = x.ticks(4);
            return ticks.map((val, i) => (
              <g key={i} transform={`translate(${x(val)},0)`}>
                <line
                  y1={railY - 36}
                  y2={railY + 36}
                  className="stroke-gray-200/70"
                  opacity={0.32}
                />
                <text
                  y={railY - 50}
                  className="fill-black text-[12px]"
                  textAnchor="middle"
                >
                  {d3.format('~r')(val)}
                </text>
              </g>
            ));
          })()}

          <rect x={0} y={railY - railH/2} width={innerW} height={railH} rx={railH/2} fill="url(#gwpRailGrad)" />

          {(() => (
            <g transform={`translate(${thresholdX},0)`}>
              <path d={`M0,${railY - railH/2 - 8} l6,-8 h-12 z`} className="fill-red-500" />
              <line y1={railY - railH/2 - 8} y2={railY - 2} className="stroke-red-400" />
              <foreignObject x={-56} y={railY - railH/2 - 30} width={112} height={22} style={{ overflow: 'visible' }}>
                <div xmlns="http://www.w3.org/1999/xhtml" className="text-[11px] text-center text-red-600 bg-white/90 rounded px-1 border">
                  Threshold: {threshold}
                </div>
              </foreignObject>
            </g>
          ))()}

          <g transform={`translate(${x(buildingGWP)},0)`}>
            <defs>
              <linearGradient id="bGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#059669"/>
                <stop offset="100%" stopColor="#0ea5a6"/>
              </linearGradient>
            </defs>
            <rect x={-2} y={railY - railH/2 - 18} width={4} height={railH + 36} rx={2}
              fill={buildingGWP > threshold ? '#ef4444' : 'url(#bGrad)'} />
            <foreignObject x={-44} y={railY - railH/2 - 32} width={88} height={24} style={{ overflow: 'visible' }}>
              <div xmlns="http://www.w3.org/1999/xhtml" className={`text-[11px] px-2 py-1 rounded text-center text-white ${buildingGWP > threshold ? 'bg-red-600' : 'bg-emerald-600'}`}>
                {buildingGWP.toFixed(2)}
              </div>
            </foreignObject>
            <foreignObject x={-52} y={railY + 20} width={104} height={22} style={{ overflow: 'visible' }}>
              <div xmlns="http://www.w3.org/1999/xhtml" className="text-[11px] px-2 py-1 rounded bg-white border text-center text-gray-800">
                Your Building
              </div>
            </foreignObject>
          </g>

          {placed.map((t) => {
            const rec = layoutMap.get(t.id);
            if (!rec || rec.hidden) return null;

            const anchorX = t.anchorX ?? t.x;
            const labelTopY = railY + t.yOffset - 11;
            const mode = (LABEL_ANCHOR_OVERRIDES && LABEL_ANCHOR_OVERRIDES[t.id]) || 'center';

            const lineX =
              mode === 'left-edge'
                ? rec.boxLeft
                : mode === 'right-edge'
                  ? rec.boxLeft + rec.w
                  : anchorX;

            const labelText = rec.text;

            return (
              <g key={t.id} className="group">
                <line
                  x1={lineX}
                  x2={lineX}
                  y1={railY + 2}
                  y2={labelTopY}
                  className="stroke-gray-300"
                />
                <circle cx={lineX} cy={railY + 1} r={2} className="fill-gray-500/70" />

                <foreignObject
                  x={rec.boxLeft}
                  y={labelTopY}
                  width={rec.w}
                  height={24}
                  style={{ overflow: 'visible' }}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    className={`relative z-10 text-[11px] px-1 py-0.5 rounded-full bg-white/95 border text-gray-700 whitespace-nowrap ${rec.alignClass || 'text-center'}`}
                    title={`${t.label}: ${t.target}`}
                  >
                    {labelText}
                  </div>
                </foreignObject>

                <foreignObject
                  x={lineX - 26}
                  y={railY - railH / 2 - 30}
                  width={52}
                  height={20}
                  style={{ overflow: 'visible' }}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    className="relative z-20 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] px-1.5 py-0.5 rounded bg-white border text-center"
                  >
                    {t.target}
                  </div>
                </foreignObject>
              </g>
            );
          })}

        </g>
      </svg>
    </div>
  );
}

const CITY_COLORS = {
  sky: '#d8dcd1',
  city1: '#2b4331',
  city2: '#3d5741',
  city3: '#466347',
  city4: '#4d674c',
  city5: '#597356',
  mountain1: '#617e58',
  mountain2: '#7b9268',
  mountain3: '#8ba078',
  mountain4: '#9bb088',
  sun: '#d19d2c',
  cloud: '#e3eae0'
};

const BASE_RING_STOPS = [
  CITY_COLORS.city5,
  CITY_COLORS.sun,
  CITY_COLORS.city2
];

const makeSeamlessRing = (angleDeg = 0) => {
  const [c1, c2, c3] = BASE_RING_STOPS;
  const epsilonTurn = 0.00012;
  const fromTurn = angleDeg / 360 + epsilonTurn;
  return `conic-gradient(from ${fromTurn}turn at 50% 50%,
    ${c1} 0turn,
    ${c2} 0.3334turn,
    ${c3} 0.6667turn,
    ${c1} 1turn)`;
};

const RING_SIZE_PX = 56;
const RING_THICK_PX = 10;

const formatKwhValue = (value) => {
  if (!Number.isFinite(value)) return '0';
  if (value >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (value >= 100) return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const normalizeName = (value) => value?.trim().toLowerCase() || '';

// === CRREM targets: load and lookup from Excel ===================

const CRREM_XLSX_URL = '/data/CRREMtargets/CRREMtargets.xlsx';
const CRREM_YEAR_START = 2020;
const CRREM_YEAR_END = 2050;
const CRREM_YEARS = Array.from(
  { length: CRREM_YEAR_END - CRREM_YEAR_START + 1 },
  (_, index) => CRREM_YEAR_START + index,
);

let crremTargetsCache = null;
let crremTargetsPromise = null;

const CRREM_USE_CODES = ['OFF', 'RET', 'HOT', 'HEA', 'RESMF'];

const CRREM_USE_LABEL_MAP = {
  OFF: 'Office',
  RET: 'Retail, Shopping Center',
  HOT: 'Hotel',
  HEA: 'Healthcare',
  RESMF: 'Residential Multi-Family',
};

function normalizeCrremString(value) {
  return (value || '').toString().trim().toLowerCase();
}

function safeCrremString(value) {
  if (value == null) return '';
  return String(value);
}

// Try to infer CRREM use codes from the building typology string
function inferCrremUseCodes(typologyHint) {
  const codes = new Set();
  const raw = typologyHint || '';
  const norm = normalizeCrremString(raw);
  const maybeCode = raw.trim().toUpperCase();

  if (CRREM_USE_CODES.includes(maybeCode)) {
    codes.add(maybeCode);
  }

  if (!norm) return Array.from(codes);

  const addIfMatch = (code, ...needles) => {
    for (const needle of needles) {
      if (norm.includes(needle)) {
        codes.add(code);
        break;
      }
    }
  };

  addIfMatch('OFF', 'office');
  addIfMatch('RET', 'retail', 'shopping');
  addIfMatch('HOT', 'hotel', 'hospitality');
  addIfMatch('HEA', 'healthcare', 'hospital', 'clinic');
  addIfMatch('RESMF', 'residential', 'apartment', 'multi family', 'multi-family', 'multifamily');

  return Array.from(codes);
}

function buildCrremMaps(workbook) {
  const ghgSheet = workbook.Sheets.WB_GHG || workbook.Sheets['WB_GHG'];
  const energySheet = workbook.Sheets.WB_ENERGY || workbook.Sheets['WB_ENERGY'];

  if (!ghgSheet || !energySheet) {
    throw new Error('CRREM workbook is missing WB_GHG or WB_ENERGY sheet');
  }

  const ghgRows = XLSX.utils.sheet_to_json(ghgSheet, { defval: null });
  const energyRows = XLSX.utils.sheet_to_json(energySheet, { defval: null });

  const makeKey = (country, useCode) =>
    `${normalizeCrremString(country)}|${(useCode || '').toString().trim().toUpperCase()}`;

  const ghgMap = new Map();
  ghgRows.forEach((row) => {
    const country = row.country || row.country_name || row.country_code;
    const useCode = row.use_code || row.use || row.use_type;
    if (!country || !useCode) return;
    const key = makeKey(country, useCode);
    ghgMap.set(key, row);
  });

  const energyMap = new Map();
  energyRows.forEach((row) => {
    const country = row.country || row.country_name || row.country_code;
    const useCode = row.use_code || row.use || row.use_type;
    if (!country || !useCode) return;
    const key = makeKey(country, useCode);
    energyMap.set(key, row);
  });

  return { ghgMap, energyMap, ghgRows, energyRows };
}

async function prepareCrremTargetsData() {
  if (crremTargetsCache) return crremTargetsCache;
  if (crremTargetsPromise) return crremTargetsPromise;

  crremTargetsPromise = fetch(CRREM_XLSX_URL)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch CRREM targets workbook (${response.status})`);
      }
      return response.arrayBuffer();
    })
    .then((buffer) => {
      const workbook = XLSX.read(buffer, { type: 'array' });
      const maps = buildCrremMaps(workbook);
      crremTargetsCache = { ...maps, years: CRREM_YEARS };
      return crremTargetsCache;
    })
    .catch((error) => {
      console.error('Unable to load CRREM targets workbook', error);
      crremTargetsCache = null;
      throw error;
    })
    .finally(() => {
      crremTargetsPromise = null;
    });

  return crremTargetsPromise;
}

function findCrremTargetsForBuilding({ data, countryName, typologyHint }) {
  if (!data || !countryName) return null;

  const normCountry = normalizeCrremString(countryName);
  if (!normCountry) return null;

  const candidateCountry = (() => {
    let match = data.ghgRows.find(
      (row) => normalizeCrremString(row.country) === normCountry,
    );
    if (match) {
      return {
        name: safeCrremString(match.country),
        code: safeCrremString(match.country_code),
      };
    }

    match = data.ghgRows.find(
      (row) => normalizeCrremString(row.country_code) === normCountry,
    );
    if (match) {
      return {
        name: safeCrremString(match.country),
        code: safeCrremString(match.country_code),
      };
    }

    match = data.ghgRows.find((row) => {
      const current = normalizeCrremString(row.country);
      return current && (current.includes(normCountry) || normCountry.includes(current));
    });
    if (match) {
      return {
        name: safeCrremString(match.country),
        code: safeCrremString(match.country_code),
      };
    }

    return null;
  })();

  if (!candidateCountry) {
    return null;
  }

  const years = data.years || CRREM_YEARS;
  const useCandidates = inferCrremUseCodes(typologyHint);
  const codesToTry = useCandidates.length ? useCandidates : CRREM_USE_CODES;

  for (const code of codesToTry) {
    const key = `${normalizeCrremString(candidateCountry.name)}|${code}`;
    const ghgRow = data.ghgMap.get(key);
    if (!ghgRow) continue;
    const energyRow = data.energyMap.get(key) || null;

    const ghgSeries = years.map((year) => ({
      year,
      value: ghgRow[year] != null ? Number(ghgRow[year]) : null,
    }));

    const energySeries = years.map((year) => ({
      year,
      value: energyRow && energyRow[year] != null ? Number(energyRow[year]) : null,
    }));

    return {
      country: candidateCountry.name,
      countryCode: candidateCountry.code,
      useCode: code,
      useLabel: ghgRow.use_type || CRREM_USE_LABEL_MAP[code] || typologyHint || '',
      years,
      ghgSeries,
      energySeries,
      unitGhg: safeCrremString(ghgRow.unit),
      unitEnergy: energyRow ? safeCrremString(energyRow.unit) : '',
    };
  }

  return null;
}

function getCrremShortTermTargets(match) {
  if (!match || !match.years || !match.ghgSeries) return [];

  const currentYear = new Date().getFullYear();
  const yearsToShow = [currentYear, currentYear + 1, currentYear + 2];

  const ghgByYear = new Map(
    match.ghgSeries.map((point) => [point.year, point.value]),
  );
  const energyByYear = new Map(
    (match.energySeries || []).map((point) => [point.year, point.value]),
  );

  return yearsToShow
    .map((year) => {
      const ghgRaw = ghgByYear.get(year);
      const energyRaw = energyByYear.get(year);

      const ghgValue = Number.isFinite(ghgRaw) ? Number(ghgRaw) : null;
      const energyValue = Number.isFinite(energyRaw) ? Number(energyRaw) : null;

      if (!Number.isFinite(ghgValue) && !Number.isFinite(energyValue)) return null;

      return { year, ghgValue, energyValue };
    })
    .filter(Boolean);
}

function InterestingFacts({ totalKg, compactTop = false }) {
  const FLIGHT_EMISSION_KG_PER_PKM = 0.083;
  const FLIGHT_TRIP_KM = 16000;
  const FLIGHT_TRIP_KG = FLIGHT_TRIP_KM * FLIGHT_EMISSION_KG_PER_PKM;

  const CAR_EMISSION_KG_PER_KM = 0.16;
  const CAR_TRIP_KM = 1500;
  const CAR_TRIP_KG = CAR_TRIP_KM * CAR_EMISSION_KG_PER_KM;

  const TREE_PER_YEAR_KG = 22;

  const flights = Math.max(0, Math.floor(totalKg / FLIGHT_TRIP_KG));
  const carTrips = Math.max(0, Math.floor(totalKg / CAR_TRIP_KG));
  const treesNeeded = Math.max(0, Math.ceil(totalKg / TREE_PER_YEAR_KG));

  const fmt = (n) => n.toLocaleString();

  const items = [
    {
      id: 'flights',
      title: 'Flights Denmark → Australia',
      value: fmt(flights),
      suffix: ' trips',
      source: 'EEA/EASA 2025: ~0.083 kg CO2/pkm; Distance ~16,000 km per flight',
      Icon: Plane,
      ringAngle: 0,
      iconColor: CITY_COLORS.city1,
      barColor: CITY_COLORS.city3
    },
    {
      id: 'car',
      title: 'Car trips Barcelona → Berlin',
      value: fmt(carTrips),
      suffix: ' trips',
      source: 'EEA 2024-2025: ~0.16 kg CO2/km (mid-size petrol); Distance ~1,500 km per trip',
      Icon: Car,
      ringAngle: 120,
      iconColor: CITY_COLORS.city1,
      barColor: CITY_COLORS.city4
    },
    {
      id: 'trees',
      title: 'Trees to offset (1 year)',
      value: fmt(treesNeeded),
      suffix: ' trees',
      source: 'EEA: ~22 kg CO2 absorbed per tree/year (temperate EU average)',
      Icon: Leaf,
      ringAngle: 240,
      iconColor: CITY_COLORS.city1,
      barColor: CITY_COLORS.city5
    },
  ];

  const containerTopClass = compactTop ? 'mt-0 sm:mt-0' : 'mt-12 sm:mt-10';

  return (
    <div className={containerTopClass}>
      <div className="mb-4 text-center mt-2 sm:text-left">
        <span className="block text-m font-semibold text-emerald-700 uppercase tracking-wide mb-2">Interesting facts</span>
        <span className="block text-sm text-gray-500">Your building&apos;s footprint converts into these daily equivalents.</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {items.map(({ id, title, value, suffix = '', source, Icon, ringAngle, iconColor, barColor }) => (
          <div
            key={id}
            className="relative overflow-hidden rounded-xl border border-emerald-100 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md transition"
          >
            <div
              className="absolute -top-10 -right-10 h-20 w-20 rounded-full opacity-20 pointer-events-none"
              style={{ backgroundColor: CITY_COLORS.cloud }}
            />
            <div className="p-4 flex items-center gap-4">
              <div
                className="relative shrink-0 rounded-full"
                style={{
                  width: `${RING_SIZE_PX}px`,
                  height: `${RING_SIZE_PX}px`,
                  backgroundImage: makeSeamlessRing(ringAngle),
                  borderRadius: '9999px',
                  transform: 'translateZ(0)'
                }}
              >
                <div
                  className="absolute rounded-full bg-white border flex items-center justify-center"
                  style={{
                    inset: `${RING_THICK_PX}px`,
                    borderColor: 'rgba(0,0,0,0.06)'
                  }}
                >
                  <div className="rounded-full bg-white/95 p-2">
                    <Icon className="w-5 h-5" style={{ color: iconColor }} />
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <div className="text-xs text-gray-500">{title}</div>
                <div className="text-2xl font-bold text-gray-900 leading-tight">{value}{suffix}</div>
                <InfoBadge tooltip={source} />
              </div>
            </div>

            <div className="px-4 pb-3">
              <div
                className="h-1.5 w-full rounded-full"
                style={{ backgroundColor: barColor }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-[11px] text-gray-500 -mb-5">
        Factors: Flights ~0.083 kg CO2/pkm (EEA/EASA 2025); Car ~0.16 kg CO2/km (EEA); Tree ~22 kg CO2/year (EEA).
      </div>
    </div>
  );
}

function ElectricityMixExplorer({ defaultCountry }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [fossilMetadata, setFossilMetadata] = useState(null);
  const [entityMap, setEntityMap] = useState(new Map());
  const [fossilEntityMap, setFossilEntityMap] = useState(new Map());
  const [entitiesList, setEntitiesList] = useState([]);
  const [selectedEntityName, setSelectedEntityName] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const barChartsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    prepareElectricityMixData()
      .then((data) => {
        if (cancelled) return;
        setEntityMap(data.entityMap);
        setEntitiesList(data.entitiesList);
        setMetadata(data.metadata);
        setFossilEntityMap(data.fossilEntityMap);
        setFossilMetadata(data.fossilMetadata);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load electricity mix', err);
        setError('Unable to load electricity mix right now. Please try again later.');
        setEntityMap(new Map());
        setEntitiesList([]);
        setMetadata(null);
        setFossilEntityMap(new Map());
        setFossilMetadata(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClick = (event) => {
      if (!suggestionsRef.current) return;
      if (!suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (!entitiesList.length) return;
    const normalized = normalizeName(defaultCountry);
    if (!normalized) return;
    const match =
      entitiesList.find((entity) => entity.nameLower === normalized) ||
      entitiesList.find((entity) => entity.codeLower && entity.codeLower === normalized) ||
      entitiesList.find((entity) => entity.nameLower.includes(normalized));
    if (match) {
      setSelectedEntityName(match.name);
      setQuery(match.name);
    }
  }, [defaultCountry, entitiesList]);

  const selectedEntity = selectedEntityName ? entityMap.get(selectedEntityName) : null;
  const availableYears = selectedEntity?.series ?? [];

  useEffect(() => {
    if (!availableYears.length) {
      setSelectedYear(null);
      return;
    }
    const latest = availableYears[availableYears.length - 1].year;
    setSelectedYear((prev) => {
      if (prev && availableYears.some((entry) => entry.year === prev)) {
        return prev;
      }
      return latest;
    });
  }, [availableYears]);

  const sliderDomain = availableYears.length ? [availableYears[0].year, availableYears[availableYears.length - 1].year] : null;

  const selectedYearRecord = useMemo(() => {
    if (!availableYears.length) return null;
    if (selectedYear == null) return availableYears[availableYears.length - 1];
    return availableYears.find((entry) => entry.year === selectedYear) || availableYears[availableYears.length - 1];
  }, [availableYears, selectedYear]);

  const segments = useMemo(() => {
    if (!selectedYearRecord) return [];
    const total = selectedYearRecord.total || 0;
    return ELECTRICITY_SOURCE_DEFS.map((source) => {
      const value = selectedYearRecord.values[source.key] ?? 0;
      return {
        ...source,
        value,
        share: total > 0 ? value / total : 0,
      };
    });
  }, [selectedYearRecord]);

  const shareSummary = useMemo(() => {
    const buckets = [
      { id: 'fossil', label: 'Fossil fuels', filter: (segment) => segment.group === 'Fossil' },
      { id: 'firm', label: 'Firm low-carbon', filter: (segment) => segment.group === 'Firm low-carbon' },
      { id: 'renewables', label: 'Renewables', filter: (segment) => segment.group === 'Renewable' },
    ];
    return buckets.map((bucket) => {
      const share = segments.reduce((acc, segment) => acc + (bucket.filter(segment) ? segment.share : 0), 0);
      return { ...bucket, share };
    });
  }, [segments]);

  const fossilSelectedEntity = selectedEntityName ? fossilEntityMap.get(selectedEntityName) : null;
  const fossilSelectedYearRecord = useMemo(
    () => getSeriesRecordForYear(fossilSelectedEntity?.series, selectedYear),
    [fossilSelectedEntity, selectedYear]
  );

  const fossilSegments = useMemo(() => {
    if (!fossilSelectedYearRecord) return [];
    const total = Object.values(fossilSelectedYearRecord.values || {}).reduce((acc, v) => acc + v, 0);
    return FOSSIL_SOURCE_DEFS.map((source) => {
      const value = fossilSelectedYearRecord.values?.[source.key] ?? 0;
      return {
        ...source,
        value,
        share: total > 0 ? value / total : 0,
      };
    });
  }, [fossilSelectedYearRecord]);

  const fossilTotalValue = fossilSegments.reduce((acc, segment) => acc + segment.value, 0);

  const lastUpdatedLabel = useMemo(
    () => getMetadataLastUpdatedLabel(metadata, ELECTRICITY_SOURCE_DEFS),
    [metadata]
  );
  const fossilLastUpdatedLabel = useMemo(
    () => getMetadataLastUpdatedLabel(fossilMetadata, FOSSIL_SOURCE_DEFS),
    [fossilMetadata]
  );

  const updateNotes = useMemo(() => lastUpdatedLabel || '', [lastUpdatedLabel]);

  const showFossilPanel = false;

  const filteredSuggestions = useMemo(() => {
    const term = normalizeName(query);
    if (!term) {
      return entitiesList.slice(0, 15);
    }
    return entitiesList
      .filter(
        (entity) =>
          entity.nameLower.includes(term) ||
          (entity.codeLower && entity.codeLower.includes(term)),
      )
      .slice(0, 20);
  }, [entitiesList, query]);

  const sliderValue = selectedYear != null ? [selectedYear] : sliderDomain ? [sliderDomain[1]] : [0];
  const totalText = selectedYearRecord?.total
    ? `${formatKwhValue(selectedYearRecord.total)} kWh/person total`
    : 'No per-capita data';
  const handleDownloadBarCharts = useCallback(async () => {
    if (!barChartsRef.current) return;
    try {
    const html2canvasModule = await import('html2canvas');
    const html2canvas = html2canvasModule.default || html2canvasModule.html2canvas || html2canvasModule;
    const pixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const exportScale = Math.max(3, pixelRatio * 2);
    const canvas = await html2canvas(barChartsRef.current, {
      backgroundColor: null,
      scale: exportScale,
      useCORS: true,
      onclone: (clonedDoc) => {
        // Strip layout chrome so export only contains the bars + legend
        const exportRoot = clonedDoc.querySelector('[data-export-root="electricity-mix-bars"]');
        if (!exportRoot) return;
        exportRoot.style.background = 'transparent';
        exportRoot.style.border = 'none';
        exportRoot.style.boxShadow = 'none';
        exportRoot.querySelectorAll('[data-export-hide="true"]').forEach((node) => {
          node.style.display = 'none';
        });
        exportRoot.querySelectorAll('[data-export-strip-bg="true"]').forEach((node) => {
          node.style.background = 'transparent';
          node.style.border = '0';
          node.style.boxShadow = 'none';
        });
      },
    });
      const dataUrl = canvas.toDataURL('image/png');
      const yearLabel = selectedYear ?? selectedYearRecord?.year ?? 'latest';
      const normalizedName = (selectedEntityName || 'electricity-mix')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${normalizedName || 'electricity-mix'}-${yearLabel}-bars.png`;
      link.click();
    } catch (err) {
      console.error('Failed to download electricity mix bars', err);
    }
  }, [selectedEntityName, selectedYear, selectedYearRecord]);

  return (
    <div className="space-y-6 text-[#2b4331]">
      <div className="space-y-2 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.45em] text-[#2f6046]">Grid context</p>
      </div>
      <div className="space-y-1 text-left">
        <h4 className="text-xl font-semibold text-[#2b4331]">Electricity mix</h4>
      </div>

      {loading ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-[#d5dacc] bg-white/80 p-6 shadow-lg">
            <Skeleton className="h-6 w-36 rounded-full" />
            <div className="mt-5 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-32 rounded-2xl" />
            </div>
          </div>
          <div className="rounded-3xl border border-[#d5dacc] bg-[#f3f6f1]/80 p-6 shadow-lg">
            <Skeleton className="h-6 w-44 rounded-full" />
            <Skeleton className="mt-5 h-40 rounded-2xl" />
            <Skeleton className="mt-4 h-4 w-2/3" />
          </div>
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-[#d5dacc] bg-[#fff1f0] p-6 text-sm text-red-700 shadow-sm">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="space-y-6 rounded-3xl border border-[#d5dacc] bg-white/90 p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="electricity-country-search" className="text-sm font-semibold text-[#2b4331]">
                  Country or region
                </Label>
                <div className="relative mt-2" ref={suggestionsRef}>
                  <Input
                    id="electricity-country-search"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder="Type to search (e.g., Denmark, World)"
                    disabled={!entitiesList.length}
                    className="rounded-2xl border border-[#d5dacc] bg-white text-[#2b4331]"
                  />
                  {showSuggestions && filteredSuggestions.length > 0 && (
                    <ul className="absolute z-20 mt-2 max-h-60 w-full overflow-y-auto rounded-2xl border border-[#d5dacc] bg-white text-sm shadow-xl">
                      {filteredSuggestions.map((entity) => (
                        <li key={entity.name}>
                          <button
                            type="button"
                            className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-[#e0e5db]"
                            onClick={() => {
                              setSelectedEntityName(entity.name);
                              setQuery(entity.name);
                              setShowSuggestions(false);
                            }}
                          >
                            <span className="text-[#2b4331]">{entity.name}</span>
                            {entity.code ? (
                              <span className="text-xs uppercase text-[#4f6b5a]">{entity.code}</span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3 border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef3ea] disabled:cursor-not-allowed"
                  onClick={handleDownloadBarCharts}
                  disabled={!selectedYearRecord}
                >
                  Download graph
                </Button>
              </div>

              <div>
                <Label htmlFor="electricity-year-slider" className="text-sm font-semibold text-[#2b4331]">
                  Reference year
                </Label>
                {!sliderDomain ? (
                  <div className="mt-2 rounded-2xl border border-[#d5dacc] bg-[#f1f4ec] p-4 text-xs text-[#4f6b5a]">
                    Select a country to enable the slider.
                  </div>
                ) : (
                  <div className="mt-2 rounded-2xl border border-[#d5dacc] bg-gradient-to-br from-[#f6faf6] to-white px-4 py-3">
                    <Slider
                      id="electricity-year-slider"
                      min={sliderDomain[0]}
                      max={sliderDomain[1]}
                      step={1}
                      value={sliderValue}
                      onValueChange={(value) => {
                        if (!value || !value.length) return;
                        setSelectedYear(Math.round(value[0]));
                      }}
                      aria-label="Electricity mix year"
                      className="mt-1"
                    />
                    <div className="mt-3 flex items-center justify-between text-xs text-[#2f6046]">
                      <span>{sliderDomain[0]}</span>
                      <span className="rounded-full bg-white/90 px-3 py-0.5 text-sm font-semibold text-[#2b4331]">
                        {selectedYear ?? sliderDomain[1]}
                      </span>
                      <span>{sliderDomain[1]}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

              {selectedYearRecord ? (
                <div className="relative">
                  <div ref={barChartsRef} className="space-y-6" data-export-root="electricity-mix-bars">
                    <div
                      className="rounded-3xl border border-[#d5dacc] bg-[#fdfdf8] p-4 space-y-4"
                      data-export-strip-bg="true"
                    >
                      <div
                        className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
                        data-export-hide="true"
                      >
                        <div>
                          <p className="text-sm font-semibold text-[#2b4331]">{selectedEntityName}</p>
                          <p className="text-xs uppercase tracking-wide text-[#4f6b5a]">
                            Per-capita mix - {selectedYearRecord.year}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-[#4f6b5a]">
                          <span>{totalText}</span>
                          <span className="relative group inline-flex items-center">
                            <Info className="w-4 h-4 text-[#2b4331]" />
                            <span className="pointer-events-none absolute right-0 top-full z-20 mt-1 w-max max-w-[220px] rounded border border-[#d5dacc] bg-white px-2 py-1 text-[11px] text-[#2b4331] shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
                              Total per-capita electricity generated that year, summing all sources shown in the bar.
                            </span>
                          </span>
                        </div>
                      </div>
                      <div
                        className="h-12 overflow-hidden border border-[#c7cfbe] bg-[#f1f4ec] text-[11px] font-medium uppercase tracking-wide text-[#2b4331]"
                        style={{ borderRadius: '22px' }}
                      >
                        <div className="flex h-full overflow-visible">
                          {segments.map((segment) => {
                            const width = segment.share * 100;
                            const minWidthNeeded = Math.max(4, Math.min(28, segment.label.length * 1.4));
                            const showLabel = width >= minWidthNeeded;
                            return (
                            <div
                              key={segment.key}
                              className="grid h-full place-items-center text-center whitespace-nowrap transition-transform duration-200 ease-out hover:-translate-y-[0.25rem]"
                              style={{
                                width: `${width}%`,
                                minWidth: width > 0 ? `${Math.max(width, 0.5)}%` : '0%',
                                padding: width > 0 ? '0 4px' : 0,
                                backgroundColor: segment.color,
                                color: showLabel ? '#f8fafc' : 'transparent',
                                lineHeight: 1,
                              }}
                              title={`${segment.label}: ${formatKwhValue(segment.value)} kWh/person (${(segment.share * 100).toFixed(1)}%)`}
                            >
                              {showLabel ? segment.label : ''}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div
                          className="h-10 overflow-hidden border border-[#c7cfbe] bg-[#dce4d4] text-[11px] font-medium uppercase tracking-wide text-[#f8fafc]"
                          style={{ borderRadius: '22px' }}
                        >
                          <div className="flex h-full overflow-visible">
                          {shareSummary.map((group) => {
                            const width = group.share * 100;
                            const color = SUMMARY_BAR_COLORS[group.id] || '#2b4331';
                            const shortLabel = SUMMARY_BAR_LABELS[group.id] || group.label;
                            const showLabel = width >= Math.max(5, shortLabel.length * 1.1);
                            return (
                              <div
                                key={group.id}
                                className="grid h-full place-items-center text-center whitespace-nowrap transition-transform duration-200 ease-out hover:-translate-y-[0.25rem]"
                                style={{
                                  width: `${width}%`,
                                  minWidth: width > 0 ? `${Math.max(width, 0.5)}%` : '0%',
                                  padding: width > 0 ? '0 4px' : 0,
                                  backgroundColor: color,
                                  color: showLabel ? '#f8fafc' : 'transparent',
                                  lineHeight: 1,
                                }}
                                title={`${group.label}: ${(group.share * 100).toFixed(1)}%`}
                              >
                                {showLabel ? `${shortLabel} ${(group.share * 100).toFixed(0)}%` : ''}
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                      {segments.map((segment) => (
                        <div
                          key={segment.key}
                          className="flex items-center justify-between rounded-xl border border-[#d5dacc] bg-white/90 px-2.5 py-1.5 text-xs sm:text-sm"
                        >
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <span className="h-2 w-2 rounded-full sm:h-2.5 sm:w-2.5" style={{ backgroundColor: segment.color }} />
                            <div className="flex items-center gap-1">
                              <span className="font-medium text-[#2b4331]">{segment.label}</span>
                              {segment.infoLabel && (
                                <span className="relative flex group">
                                  <Info className="h-3 w-3 text-[#4f6b5a]" />
                                  <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 -translate-x-1/2 whitespace-nowrap rounded border border-[#d5dacc] bg-white px-2 py-1 text-[10px] text-[#2b4331] shadow-lg opacity-0 group-hover:opacity-100">
                                    {segment.infoLabel}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-[#4f6b5a]">
                            {(segment.share * 100).toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-[#d5dacc] bg-[#f7faf4] p-5 text-sm text-[#4f6b5a]">
                  Select a country to unlock the electricity mix visualization. We only show the chart once data is available.
                </div>
              )}
          <div className="mt-4 text-center text-xs text-[#4f6b5a]">
            Data source:{' '}
            <span
              className="cursor-help underline decoration-dotted font-semibold text-[#2b4331]"
              title="Our World in Data per-capita electricity datasets (2025)."
            >
              ourworldindata.org
            </span>
            {updateNotes ? (
              <> - <span className="font-semibold text-[#2b4331]">{updateNotes}</span></>
            ) : null}
          </div>

        </div>

        {showFossilPanel && (
            <div className="space-y-5 rounded-3xl border border-[#d5dacc] bg-[#eef2e7]/90 p-6 shadow-lg">
              <div className="flex items-center justify-between text-sm font-semibold text-[#2b4331]">
                <span>Fossil • Nuclear • Renewables</span>
                <span className="text-xs uppercase tracking-[0.4em] text-[#4f6b5a]">
                  {fossilSelectedYearRecord?.year ?? selectedYear ?? '–'}
                </span>
              </div>
              <div className="h-14 overflow-hidden rounded-full border border-[#c7cfbe] bg-[#dfe5d8]">
                <div className="flex h-full w-full">
                  {fossilSegments.length ? (
                    fossilSegments.map((segment) => (
                      <div
                        key={segment.key}
                        className="h-full"
                        title={`${segment.label}: ${formatKwhValue(segment.value)} kWh/person (${(segment.share * 100).toFixed(1)}%)`}
                        style={{
                          width: `${(segment.share * 100).toFixed(2)}%`,
                          minWidth: segment.share > 0 ? '2%' : '0%',
                          backgroundColor: segment.color,
                        }}
                      />
                    ))
                  ) : (
                    <div className="h-full w-full bg-[#c7cfbe]" />
                  )}
                </div>
              </div>
              <div className="text-sm">
                <p className="text-[11px] uppercase tracking-[0.45em] text-[#4f6b5a]">Per-capita total</p>
                <p className="text-2xl font-semibold text-[#2b4331]">
                  {fossilTotalValue ? `${formatKwhValue(fossilTotalValue)} kWh` : 'Data unavailable'}
                </p>
                <p className="text-xs text-[#4f6b5a]">
                  {selectedEntityName ? `Showing ${selectedEntityName}` : 'Select a location to unlock this breakdown.'}
                </p>
              </div>
              <div className="space-y-3">
                {fossilSegments.length ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {fossilSegments.map((segment) => (
                      <div
                        key={segment.key}
                        className="rounded-xl border border-[#c7cfbe] bg-white/80 px-3 py-2 text-center text-xs text-[#2b4331]"
                      >
                        <div className="uppercase tracking-[0.3em] text-[#4f6b5a]">{segment.label}</div>
                        <div className="mt-1 text-lg font-semibold text-[#2f6046]">
                          {formatKwhValue(segment.value)} kWh
                        </div>
                        <div className="text-[11px] text-[#4f6b5a]">{(segment.share * 100).toFixed(1)}%</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-[#d5dacc] bg-white/60 px-3 py-4 text-center text-xs text-[#4f6b5a]">
                    Fossil / nuclear / renewable data will appear once a country is selected.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      )}
    </div>
  );
}




function CrremShortTermBenchmark({ buildingGwp, summary }) {
  const buildingValue = Number.isFinite(buildingGwp) ? buildingGwp : null;
  const shortTargets = summary?.targets || [];

  const ghgTargets = shortTargets.filter((t) => Number.isFinite(t.ghgValue));
  const energyTargets = shortTargets.filter((t) => Number.isFinite(t.energyValue));

  if (buildingValue == null || !ghgTargets.length) return null;

  const buildingColor = '#059669';
  const buildingAlertColor = '#dc2626';
  const ghgTargetColor = CITY_COLORS.city2 || '#3d5741';
  const energyTargetColor = CITY_COLORS.mountain3 || '#8ba078';

  const formatValue = (value) => {
    if (!Number.isFinite(value)) return '';
    if (value >= 100) return value.toFixed(0);
    if (value >= 10) return value.toFixed(1);
    return value.toFixed(2);
  };

  const makeScale = (values) => {
    const clean = values.filter((v) => Number.isFinite(v) && v > 0);
    const max = clean.length ? Math.max(...clean) : 0;
    if (!max) return () => 0;

    const paddedMax = max * 1.05;

    return (value) => {
      if (!Number.isFinite(value) || value <= 0) return 0;
      const raw = (value / paddedMax) * 100;
      const clamped = Math.max(2, Math.min(98, raw));
      return clamped;
    };
  };

  const hasBuildingEnergyData =
    Number.isFinite(Number(summary?.buildingEnergyIntensity)) ||
    Number.isFinite(Number(summary?.buildingEnergy)) ||
    Number.isFinite(Number(summary?.buildingEnergyKwhPerM2));
  const buildingEnergyValue = (() => {
    const intensity = Number(summary?.buildingEnergyIntensity);
    if (Number.isFinite(intensity)) return intensity;
    const perM2 = Number(summary?.buildingEnergyKwhPerM2);
    if (Number.isFinite(perM2)) return perM2;
    const energy = Number(summary?.buildingEnergy);
    if (Number.isFinite(energy)) return energy;
    return null;
  })();

  const ghgValuesForScale = [buildingValue, ...ghgTargets.map((t) => t.ghgValue)];
  const energyValuesForScale = [
    ...energyTargets.map((t) => t.energyValue),
    ...(Number.isFinite(buildingEnergyValue) ? [buildingEnergyValue] : [])
  ];

  const ghgScale = makeScale(ghgValuesForScale);
  const energyScale = makeScale(energyValuesForScale);

  const currentGhgTarget = ghgTargets[0];
  const currentEnergyTarget = energyTargets[0];
  const isUnderCurrentEnergyTarget =
    hasBuildingEnergyData && currentEnergyTarget && Number.isFinite(currentEnergyTarget.energyValue)
      ? buildingEnergyValue <= currentEnergyTarget.energyValue
      : null;
  const isUnderCurrentGhgTarget =
    currentGhgTarget && Number.isFinite(currentGhgTarget.ghgValue)
      ? buildingValue <= currentGhgTarget.ghgValue
      : null;

  const buildingPercent = ghgScale(buildingValue);
  const buildingEnergyPercent = hasBuildingEnergyData ? energyScale(buildingEnergyValue) : null;

  // layout constants
  const railTopPx = 22;
  const railHeightPx = -3;
  const railCenterPx = railTopPx + railHeightPx / 2;

  const markerLineHeight = 28;
  const markerThicknessPx = 3;

  const markerTopPx = railTopPx + railHeightPx / 2 - markerLineHeight / 2;
  const valueLabelTopPx = markerTopPx - 18;
  const yearLabelTopPx = markerTopPx + markerLineHeight + 6;

  const buildingDotSizePx = 16;
  const buildingDotTopPx = railCenterPx - buildingDotSizePx / 2;
  const ghgRailRef = useRef(null);
  const energyRailRef = useRef(null);
  const [ghgRailWidth, setGhgRailWidth] = useState(0);
  const [energyRailWidth, setEnergyRailWidth] = useState(0);

  const updateRailWidths = useCallback(() => {
    if (ghgRailRef.current) {
      setGhgRailWidth(ghgRailRef.current.offsetWidth || 0);
    }
    if (energyRailRef.current) {
      setEnergyRailWidth(energyRailRef.current.offsetWidth || 0);
    }
  }, []);

  useEffect(() => {
    updateRailWidths();
    window.addEventListener('resize', updateRailWidths);
    return () => window.removeEventListener('resize', updateRailWidths);
  }, [updateRailWidths]);

  const getPositionStyle = useCallback(
    (percent, railWidth) => {
      if (railWidth) {
        const px = Math.round((percent / 100) * railWidth);
        return { left: `${px}px`, transform: 'translateX(-50%)' };
      }
      return { left: `${percent}%`, transform: 'translateX(-50%)' };
    },
    [],
  );

  const getMarkerStyle = useCallback(
    (percent, railWidth, widthPx) => {
      if (railWidth && widthPx) {
        const centerPx = Math.round((percent / 100) * railWidth);
        return { left: `${centerPx}px`, transform: 'translateX(-50%)' };
      }
      return { left: `${percent}%`, transform: 'translateX(-50%)' };
    },
    [],
  );

  const getCenterStyle = useCallback(
    (percent, railWidth) => {
      if (railWidth) {
        const centerPx = Math.round((percent / 100) * railWidth);
        return { left: `${centerPx}px`, transform: 'translateX(-50%)' };
      }
      return { left: `${percent}%`, transform: 'translateX(-50%)' };
    },
    [],
  );

  return (
    <div className="space-y-4">
      <div className="text-center md:text-left">
        <span className="block text-m font-semibold text-emerald-700 uppercase tracking-wide mt-2 mb-2">
          CRREM Pathway
        </span>
        <span className="block text-sm text-gray-600 text-left">
        Comparing your building intensities with the CRREM pathway towards the 1.5°C Paris limit for{' '}
          {summary.country}
          {summary.useLabel ? `, ${summary.useLabel}` : ''}.
        </span>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* GHG column */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-[#2b4331]">
            GHG Intensity pathway
          </p>

          <div className="relative w-full rounded-2xl border-2 border-dashed border-[#d5dacc] bg-white px-4 pt-6 pb-3">
            <div className="relative h-20" ref={ghgRailRef}>
              {/* rail */}
              <div className="absolute left-0 right-0 top-4 h-2 rounded-full bg-[#dce4d4]" />

              {/* building fill */}
              <div
                className="absolute left-0 top-4 h-2 rounded-full"
                style={{
                  width: `${buildingPercent}%`,
                  backgroundColor:
                    isUnderCurrentGhgTarget === false ? buildingAlertColor : buildingColor
                }}
              />

              {/* building marker: dot on rail, label just below rail */}
              <div
                className="absolute flex flex-col items-center"
                style={{
                  ...getCenterStyle(buildingPercent, ghgRailWidth),
                  top: `${buildingDotTopPx}px`,
                }}
              >
                <div
                  className="rounded-full border-2 border-white shadow-sm"
                  style={{
                    backgroundColor:
                      isUnderCurrentGhgTarget === false ? buildingAlertColor : buildingColor,
                    width: buildingDotSizePx,
                    height: buildingDotSizePx,
                  }}
                />
                <span className="mt-2 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-[#2b4331]">
                  Your building
                </span>
              </div>

              {/* building numeric value above bar (aligned with other markers) */}
              <div
                className="absolute flex flex-col items-center"
                style={{
                  ...getCenterStyle(buildingPercent, ghgRailWidth),
                  top: `${valueLabelTopPx}px`,
                }}
              >
                <span
                  className="text-xs font-semibold"
                  style={{
                    color:
                      isUnderCurrentGhgTarget === false ? buildingAlertColor : buildingColor
                  }}
                >
                  {formatValue(buildingValue)}
                </span>
              </div>

              {/* CRREM GHG markers */}
              {ghgTargets.map((target, index) => {
                const leftPct = ghgScale(target.ghgValue);
                const baseStyle = getMarkerStyle(leftPct, ghgRailWidth, markerThicknessPx);
                const middleIndex = Math.floor((ghgTargets.length - 1) / 2);
                const isCenter = ghgTargets.length === 1 || index === middleIndex;
                const isFirst = index === 0 && !isCenter;
                const isLast = index === ghgTargets.length - 1 && !isCenter;

                return (
                  <Fragment key={target.year}>
                    {/* value above marker */}
                    <div
                      className="absolute text-[11px] font-semibold text-[#2b4331]"
                      style={{
                        ...getCenterStyle(leftPct, ghgRailWidth),
                        top: `${valueLabelTopPx}px`,
                      }}
                    >
                      {formatValue(target.ghgValue)}
                    </div>

                    {/* vertical marker */}
                    <div
                      className="absolute rounded-full"
                      style={{
                        ...baseStyle,
                        top: `${markerTopPx}px`,
                        width: `${markerThicknessPx}px`,
                        minWidth: `${markerThicknessPx}px`,
                        maxWidth: `${markerThicknessPx}px`,
                        height: `${markerLineHeight}px`,
                        backgroundColor: ghgTargetColor,
                      }}
                    />

                    {/* year label under bar, two line layout */}
                    <div
                      className={`absolute text-[10px] leading-tight text-[#4f6b5a] ${
                        isFirst ? 'text-left' : isLast ? 'text-right' : 'text-center'
                      }`}
                      style={{
                        ...getCenterStyle(leftPct, ghgRailWidth),
                        top: `${yearLabelTopPx}px`,
                        transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                      }}
                    >
                      <span className="block">{target.year}</span>
                      <span className="block">target</span>
                    </div>
                  </Fragment>
                );
              })}
            </div>

            {/* legend inside card */}
            <div className="mt-0 flex items-center justify-center gap-2 text-[11px] text-[#4f6b5a]">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: ghgTargetColor }}
              />
              <span>
                CRREM GHG intensity targets ({summary.unitGhg || 'kgCO2e/m²/yr'})
              </span>
            </div>
          </div>

          {isUnderCurrentGhgTarget != null && (
            <p
              className={`text-xs text-center ${
                isUnderCurrentGhgTarget ? 'text-emerald-700' : 'text-red-600'
              }`}
            >
              {isUnderCurrentGhgTarget
                ? 'Your building is under the current CRREM GHG target.'
                : 'Your building exceeds the current CRREM GHG target.'}
            </p>
          )}
        </div>

        {/* energy column */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-[#2b4331]">
            Energy Intensity pathway
          </p>

          {energyTargets.length ? (
            <>
              <div className="relative w-full rounded-2xl border-2 border-dashed border-[#d5dacc] bg-white px-4 pt-6 pb-3">
              <div className="relative h-20" ref={energyRailRef}>
                  {/* rail */}
                  <div className="absolute left-0 right-0 top-4 h-2 rounded-full bg-[#dce4d4]" />

                  {buildingEnergyPercent != null && (
                    <>
                      <div
                        className="absolute left-0 top-4 h-2 rounded-full"
                        style={{
                          width: `${buildingEnergyPercent}%`,
                          backgroundColor:
                            isUnderCurrentEnergyTarget === false ? buildingAlertColor : buildingColor
                        }}
                      />

                      <div
                        className="absolute flex flex-col items-center"
                        style={{
                          ...getCenterStyle(buildingEnergyPercent, energyRailWidth),
                          top: `${buildingDotTopPx}px`,
                        }}
                      >
                        <div
                          className="rounded-full border-2 border-white shadow-sm"
                          style={{
                            backgroundColor:
                              isUnderCurrentEnergyTarget === false ? buildingAlertColor : buildingColor,
                            width: buildingDotSizePx,
                            height: buildingDotSizePx,
                          }}
                        />
                        <span className="mt-2 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-[#2b4331]">
                          Your building
                        </span>
                      </div>

                      <div
                        className="absolute flex flex-col items-center"
                        style={{
                          ...getCenterStyle(buildingEnergyPercent, energyRailWidth),
                          top: `${valueLabelTopPx}px`,
                        }}
                      >
                        <span
                          className="text-xs font-semibold"
                          style={{
                            color:
                              isUnderCurrentEnergyTarget === false ? buildingAlertColor : buildingColor
                          }}
                        >
                          {formatValue(buildingEnergyValue)}
                        </span>
                      </div>
                    </>
                  )}

                  {/* energy markers */}
                  {energyTargets.map((target, index) => {
                    const leftPct = energyScale(target.energyValue);
                    const baseStyle = getMarkerStyle(leftPct, energyRailWidth, markerThicknessPx);
                    const middleIndex = Math.floor((energyTargets.length - 1) / 2);
                    const isCenter = energyTargets.length === 1 || index === middleIndex;
                    const isFirst = index === 0 && !isCenter;
                    const isLast = index === energyTargets.length - 1 && !isCenter;

                    return (
                      <Fragment key={target.year}>
                        {/* value above marker */}
                        <div
                          className="absolute text-[11px] font-semibold text-[#2b4331]"
                          style={{
                            ...getCenterStyle(leftPct, energyRailWidth),
                            top: `${valueLabelTopPx}px`,
                          }}
                        >
                          {formatValue(target.energyValue)}
                        </div>

                        {/* vertical marker */}
                        <div
                          className="absolute rounded-full"
                          style={{
                            ...baseStyle,
                            top: `${markerTopPx}px`,
                            width: `${markerThicknessPx}px`,
                            minWidth: `${markerThicknessPx}px`,
                            maxWidth: `${markerThicknessPx}px`,
                            height: `${markerLineHeight}px`,
                            backgroundColor: energyTargetColor,
                          }}
                        />

                        {/* year label under bar, two line layout */}
                    <div
                      className={`absolute text-[10px] leading-tight text-[#4f6b5a] ${
                        isFirst ? 'text-left' : isLast ? 'text-right' : 'text-center'
                      }`}
                      style={{
                        ...getCenterStyle(leftPct, energyRailWidth),
                        top: `${yearLabelTopPx}px`,
                        transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                      }}
                    >
                      <span className="block">{target.year}</span>
                      <span className="block">target</span>
                    </div>
                      </Fragment>
                    );
                  })}

                  {!hasBuildingEnergyData && (
                    <div
                      className="absolute flex flex-col items-center"
                      style={{
                        left: 0,
                        top: `${buildingDotTopPx}px`,
                      }}
                    >
                      <div
                        className="rounded-full border-2 border-white shadow-sm"
                        style={{
                          backgroundColor: buildingColor,
                          width: buildingDotSizePx,
                          height: buildingDotSizePx,
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* legend inside card */}
                <div className="mt-0 flex items-center justify-center gap-2 text-[11px] text-[#4f6b5a]">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: energyTargetColor }}
                  />
                  <span>
                    CRREM Energy intensity targets ({summary.unitEnergy || 'kWh/m²/yr'})
                  </span>
                </div>
              </div>

              {hasBuildingEnergyData ? (
                isUnderCurrentEnergyTarget != null && (
                  <p
                    className={`text-xs text-center ${
                      isUnderCurrentEnergyTarget ? 'text-emerald-700' : 'text-red-600'
                    }`}
                  >
                    {isUnderCurrentEnergyTarget
                      ? 'Your building is under the current CRREM Energy target.'
                      : 'Your building exceeds the current CRREM Energy target.'}
                  </p>
                )
              ) : (
                <p className="text-xs text-center text-red-600">
                  Energy intensity data for your building is not available.
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-[#4f6b5a]">
              Energy intensity targets are not available for this CRREM selection.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function CrremTargetsTable({ countryName, buildingTypology, buildingGwp, buildingEnergyIntensity }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [targets, setTargets] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const ghgColor = CITY_COLORS.city2;       // deep green
  const energyColor = CITY_COLORS.mountain3; // softer green from skyline palette
  const buildingEnergyValue = Number.isFinite(buildingEnergyIntensity)
    ? buildingEnergyIntensity
    : null;

  // Fetch and match CRREM row based on country and typology
  useEffect(() => {
    let cancelled = false;

    if (!countryName) {
      setTargets(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    prepareCrremTargetsData()
      .then((data) => {
        if (cancelled) return;
        const match = findCrremTargetsForBuilding({
          data,
          countryName,
          typologyHint: buildingTypology,
        });
        if (!match) {
          setTargets(null);
          setError('No CRREM targets found for this country and typology.');
        } else {
          setTargets(match);
          setError(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load CRREM targets', err);
        setTargets(null);
        setError('Unable to load CRREM targets right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [countryName, buildingTypology]);

  // Build chart paths and scales once targets are available
  const chartConfig = useMemo(() => {
    if (!targets) return null;

    const years = targets.years || [];
    if (!years.length) return null;

    const ghgValues = targets.ghgSeries.map((point) =>
      Number.isFinite(point.value) ? point.value : null,
    );
    const energyValues = targets.energySeries.map((point) =>
      Number.isFinite(point.value) ? point.value : null,
    );

    const ghgDomainValues = ghgValues.filter((value) => value != null);
    const energyDomainValues = [
      ...energyValues.filter((value) => value != null),
      ...(buildingEnergyValue != null ? [buildingEnergyValue] : [])
    ];

    if (!ghgDomainValues.length && !energyDomainValues.length) {
      return null;
    }

    const svgWidth = 840;
    const svgHeight = 310;
    const padding = { top: 32, right: 70, bottom: 48, left: 60 };
    const innerWidth = svgWidth - padding.left - padding.right;
    const innerHeight = svgHeight - padding.top - padding.bottom;

    const xScale = d3
      .scaleLinear()
      .domain([years[0], years[years.length - 1]])
      .range([0, innerWidth]);

    const ghgMax = ghgDomainValues.length
      ? Math.max(...ghgDomainValues)
      : 1;
    const energyMax = energyDomainValues.length
      ? Math.max(...energyDomainValues)
      : 1;

    const ghgScale = d3
      .scaleLinear()
      .domain([0, ghgMax * 1.1])
      .range([innerHeight, 0])
      .nice();

    const energyScale = d3
      .scaleLinear()
      .domain([0, energyMax * 1.1])
      .range([innerHeight, 0])
      .nice();

    const pointData = years.map((year, index) => ({
      year,
      x: xScale(year),
      ghgValue: ghgValues[index],
      energyValue: energyValues[index],
      ghgY:
        ghgValues[index] != null
          ? ghgScale(ghgValues[index])
          : null,
      energyY:
        energyValues[index] != null
          ? energyScale(energyValues[index])
          : null,
    }));

    const ghgLine = d3
      .line()
      .defined((point) => point.ghgY != null)
      .x((point) => point.x)
      .y((point) => point.ghgY);

    const energyLine = d3
      .line()
      .defined((point) => point.energyY != null)
      .x((point) => point.x)
      .y((point) => point.energyY);

    const ghgPath = ghgLine(pointData) || '';
    const energyPath = energyLine(pointData) || '';

    const xTicks = years.filter((year, index) => {
      if (index === 0) return true;
      if (index === years.length - 1) return true;
      return year % 2 === 0;
    });

    return {
      svgWidth,
      svgHeight,
      padding,
      innerWidth,
      innerHeight,
      years,
      pointData,
      xTicks,
      ghgScale,
      energyScale,
      ghgPath,
      energyPath,
    };
  }, [targets, buildingEnergyValue]);

  // All hooks must be called before any early returns
  const currentYear = new Date().getFullYear();
  const shortTermTargets = useMemo(() => {
    if (!chartConfig || !chartConfig.pointData?.length) return [];
    const yearsToShow = [currentYear, currentYear + 1, currentYear + 2];
    const byYear = new Map(chartConfig.pointData.map((p) => [p.year, p]));
    return yearsToShow
      .map((year) => byYear.get(year))
      .filter((p) => p && Number.isFinite(p.ghgValue));
  }, [chartConfig, currentYear]);
  const buildingGwpValue = Number.isFinite(buildingGwp) ? buildingGwp : null;
  const shortTermMax = useMemo(() => {
    const values = shortTermTargets.map((p) => p.ghgValue || 0);
    if (buildingGwpValue != null) values.push(buildingGwpValue);
    return values.length ? Math.max(...values) : 0;
  }, [shortTermTargets, buildingGwpValue]);

  if (!countryName) {
    return (
      <div className="rounded-3xl border border-[#d5dacc] bg-white/95 p-5 text-sm text-[#4f6b5a]">
        Add a country in the project info to see CRREM targets here.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-3xl border border-[#d5dacc] bg-white/95 p-5 space-y-4">
        <Skeleton className="h-4 w-44 rounded-full" />
        <Skeleton className="h-5 w-80 rounded-full" />
        <Skeleton className="h-52 w-full rounded-2xl" />
        <Skeleton className="h-10 w-full rounded-2xl" />
      </div>
    );
  }

  if (error || !targets) {
    return (
      <div className="rounded-3xl border border-[#d5dacc] bg-white/95 p-5 text-sm text-red-700">
        <div className="rounded-2xl border border-dashed border-[#fca5a5] bg-[#fff1f0] px-4 py-3">
          {error || 'No CRREM targets matched this project setup.'}
        </div>
      </div>
    );
  }

  const activePoint =
    chartConfig &&
    chartConfig.pointData.length &&
    chartConfig.pointData[
      activeIndex != null
        ? Math.max(0, Math.min(activeIndex, chartConfig.pointData.length - 1))
        : chartConfig.pointData.length - 1
    ];

  const firstPoint =
    chartConfig && chartConfig.pointData.length ? chartConfig.pointData[0] : null;
  const lastPoint =
    chartConfig && chartConfig.pointData.length
      ? chartConfig.pointData[chartConfig.pointData.length - 1]
      : null;
  const currentPoint =
    chartConfig && chartConfig.pointData.length
      ? chartConfig.pointData.find((point) => point.year === currentYear) || null
      : null;
  const isUnderCurrentTarget =
    buildingGwpValue != null && currentPoint
      ? buildingGwpValue <= currentPoint.ghgValue
      : null;
  const isUnderCurrentEnergyTarget =
    buildingEnergyValue != null && currentPoint
      ? buildingEnergyValue <= currentPoint.energyValue
      : null;
  const ghgLineY =
    chartConfig && buildingGwpValue != null
      ? chartConfig.ghgScale(buildingGwpValue)
      : null;
  const energyLineY =
    chartConfig && buildingEnergyValue != null
      ? chartConfig.energyScale(buildingEnergyValue)
      : null;
  const getLineLabelY = (lineY, innerHeight) => {
    if (!Number.isFinite(lineY)) return null;
    const above = lineY - 6;
    if (above > 12) return above;
    const below = lineY + 14;
    return Math.min(innerHeight - 4, below);
  };

  const formatValue = (value) => {
    if (!Number.isFinite(value)) return 'ƒ?"';
    if (value >= 100) return value.toFixed(0);
    if (value >= 10) return value.toFixed(1);
    return value.toFixed(2);
  };

  const allYears = targets.years || [];
  const columnCount = 3;

  const buildColumnRows = (series = []) => {
    const entries = allYears.map((year, index) => ({
      year,
      value: series[index]?.value,
    }));
    const perColumn = Math.max(1, Math.ceil(entries.length / columnCount));
    const columns = Array.from({ length: columnCount }, (_, colIndex) =>
      entries.slice(colIndex * perColumn, (colIndex + 1) * perColumn)
    );
    const rowCount = Math.max(...columns.map((col) => col.length), 0);
    return Array.from({ length: rowCount }, (_, rowIndex) =>
      columns.map((col) => col[rowIndex] || null)
    );
  };

  const ghgRows = buildColumnRows(targets.ghgSeries);
  const energyRows = buildColumnRows(targets.energySeries);

  return (
    <div className="rounded-3xl border border-[#d5dacc] bg-white/95 p-5 space-y-5">
      {/* card header */}
      <div className="space-y-1 text-sm">
        <p className="text-sm font-semibold text-[#2b4331]">
          CRREM decarbonisation and energy reduction pathway
        </p>
      </div>

      <div className="space-y-1 text-sm text-[#4f6b5a]">
        <p className="text-justify">
          CRREM (Carbon Risk Real Estate Monitor) translates the Paris climate goal into
          country and building type specific pathways for GHG and Energy Intensity, so
          assets can benchmark performance and see when they become
          stranded against a 1.5 degree pathway. It is used to understand
          transition risk and plan retrofit and grid decarbonisation strategies.
        </p>
      </div>

      {/* chart plus right side info */}
      <div className="rounded-2xl border border-[#d5dacc] bg-white p-4">
        {chartConfig ? (
          <div className="space-y-1">
            <div className="flex flex-col gap-4 lg:flex-row">
              {/* chart area: about three quarters */}
              <div className="lg:w-3/4">
                <p className="-mb-3 text-center text-[13px] font-semibold text-[#2b4331]">
                  CRREM decarbonization pathway
                </p>
                <svg
                  width="100%"
                  viewBox={`0 0 ${chartConfig.svgWidth} ${chartConfig.svgHeight}`}
                  className="overflow-visible"
                >
                  <g
                    transform={`translate(${chartConfig.padding.left},${chartConfig.padding.top})`}
                  >
                    {/* x axis and vertical grid lines */}
                    <g transform={`translate(0,${chartConfig.innerHeight})`}>
                      <line
                        x1={0}
                        x2={chartConfig.innerWidth}
                        y1={0}
                        y2={0}
                        className="stroke-[#c7cfbe]"
                      />
                      {chartConfig.xTicks.map((year) => {
                        const x =
                          chartConfig.pointData.find((point) => point.year === year)?.x;
                        if (x == null) return null;
                        return (
                          <g key={year} transform={`translate(${x},0)`}>
                            <line
                              y1={0}
                              y2={-chartConfig.innerHeight}
                              className="stroke-[#e1e7dd]"
                              strokeDasharray="2 4"
                            />
                            <text
                              y={28}
                              textAnchor="middle"
                              className="fill-[#4f6b5a] text-[12px]"
                            >
                              {year}
                            </text>
                          </g>
                        );
                      })}
                    </g>

                    {/* left axis label */}
                    <text
                      x={-chartConfig.padding.left + 12}
                      y={chartConfig.innerHeight / 2}
                      transform={`rotate(-90 ${
                        -chartConfig.padding.left + 12
                      } ${chartConfig.innerHeight / 2})`}
                      className="text-[12px] font-semibold"
                      fill={ghgColor}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      GHG intensity [kgCO2e/mAı/yr]
                    </text>

                    {/* right axis label */}
                    <text
                      x={chartConfig.innerWidth + chartConfig.padding.right - 18}
                      y={chartConfig.innerHeight / 2}
                      transform={`rotate(90 ${
                        chartConfig.innerWidth + chartConfig.padding.right - 18
                      } ${chartConfig.innerHeight / 2})`}
                      className="text-[12px] font-semibold"
                      fill={energyColor}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      Energy intensity [kWh/mAı/yr]
                    </text>

                    {/* left axis ticks */}
                    {chartConfig.ghgScale.ticks(4).map((tickValue) => {
                      const y = chartConfig.ghgScale(tickValue);
                      return (
                        <g key={`ghg-${tickValue}`} transform={`translate(0,${y})`}>
                          <line
                            x1={0}
                            x2={chartConfig.innerWidth}
                            y1={0}
                            y2={0}
                            className="stroke-[#e1e7dd]"
                          />
                          <text
                            x={-8}
                            y={3}
                            textAnchor="end"
                            className="text-[12px]"
                            fill={ghgColor}
                          >
                            {formatValue(tickValue)}
                          </text>
                        </g>
                      );
                    })}

                    {/* right axis ticks */}
                    {chartConfig.energyScale.ticks(4).map((tickValue) => {
                      const y = chartConfig.energyScale(tickValue);
                      return (
                        <g
                          key={`energy-${tickValue}`}
                          transform={`translate(${chartConfig.innerWidth},${y})`}
                        >
                          <text
                            x={8}
                            y={3}
                            textAnchor="start"
                            className="text-[12px]"
                            fill={energyColor}
                          >
                            {formatValue(tickValue)}
                          </text>
                        </g>
                      );
                    })}

                    {/* data lines */}
                    <path
                      d={chartConfig.ghgPath}
                      fill="none"
                      stroke={ghgColor}
                      strokeWidth={2}
                    />
                    <path
                      d={chartConfig.energyPath}
                      fill="none"
                      stroke={energyColor}
                      strokeWidth={2}
                    />

                    {buildingEnergyValue != null && (
                      <g>
                        <line
                          x1={0}
                          x2={chartConfig.innerWidth}
                          y1={energyLineY}
                          y2={energyLineY}
                          stroke={energyColor}
                          strokeDasharray="4 4"
                          strokeWidth={1.5}
                        />
                        <text
                          x={chartConfig.innerWidth}
                          y={getLineLabelY(energyLineY, chartConfig.innerHeight)}
                          className="text-[11px] font-semibold"
                          fill={energyColor}
                          textAnchor="end"
                        >
                          Your building
                        </text>
                      </g>
                    )}

                    {buildingEnergyValue == null && buildingGwpValue != null && (
                      <g>
                        <line
                          x1={0}
                          x2={chartConfig.innerWidth}
                          y1={ghgLineY}
                          y2={ghgLineY}
                          stroke={ghgColor}
                          strokeDasharray="4 4"
                          strokeWidth={1.5}
                        />
                        <text
                          x={chartConfig.innerWidth}
                          y={getLineLabelY(ghgLineY, chartConfig.innerHeight)}
                          className="text-[11px] font-semibold"
                          fill={ghgColor}
                          textAnchor="end"
                        >
                          Your building
                        </text>
                      </g>
                    )}

                    {/* data points and hover interaction */}
                    {chartConfig.pointData.map((point, index) => (
                      <g key={point.year}>
                        {point.ghgY != null && (
                          <circle
                            cx={point.x}
                            cy={point.ghgY}
                            r={index === activeIndex ? 4 : 3}
                            className="cursor-pointer"
                            fill={ghgColor}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        )}
                        {point.energyY != null && (
                          <circle
                            cx={point.x}
                            cy={point.energyY}
                            r={index === activeIndex ? 4 : 3}
                            className="cursor-pointer"
                            fill={energyColor}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        )}
                      </g>
                    ))}
                  </g>
                </svg>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-[13px] text-[#4f6b5a]">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: ghgColor }}
                    />
                    GHG Intensity pathway
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: energyColor }}
                    />
                    Energy Intensity pathway
                  </span>
                </div>
              </div>

              {/* right info column: about one quarter */}
              <div className="lg:w-1/4 flex flex-col justify-between gap-4 text-[13px] text-[#4f6b5a]">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#2b4331]">
                    Scope and typology
                  </p>
                  <div className="space-y-1">
                    <p>
                      <span className="text-[#2b4331]">Country:</span> {targets.country}
                      {targets.countryCode ? ` (${targets.countryCode})` : ''}
                    </p>
                    <p>
                      <span className="text-[#2b4331]">Building use:</span> {targets.useLabel || targets.useCode}
                    </p>
                  </div>
                </div>

                {currentPoint ? (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-[#2b4331]">
                      Current targets
                    </p>
                    <p>
                      GHG target for {currentPoint.year}:{' '}
                      <span className="font-semibold text-black">
                        {formatValue(currentPoint.ghgValue)} {targets.unitGhg || 'kgCO2e/m2/yr'}
                      </span>
                    </p>
                    <p className="mt-1">
                      Energy target for {currentPoint.year}:{' '}
                      <span className="font-semibold text-black">
                        {formatValue(currentPoint.energyValue)} {targets.unitEnergy || 'kWh/m2/yr'}
                      </span>
                    </p>
                    {buildingGwpValue != null ? (
                      <p className="mt-1 text-[#2b4331] text-justify">
                        This project is{' '}
                        <span
                          className={`font-bold ${
                            isUnderCurrentTarget ? 'text-emerald-700' : 'text-red-600'
                          }`}
                        >
                          {isUnderCurrentTarget ? 'under' : 'exceeding'}
                        </span>{' '}
                        the current GHG target based on its GWP benchmarking value of{' '}
                        <span
                          className={`font-bold ${
                            isUnderCurrentTarget ? 'text-emerald-700' : 'text-red-600'
                          }`}
                        >
                          {formatValue(buildingGwpValue)} kgCO2e/m2/yr
                        </span>
                        .
                      </p>
                    ) : null}
                    {buildingEnergyValue != null ? (
                      <p className="mt-1 text-[#2b4331] text-justify">
                        This project is{' '}
                        <span
                          className={`font-bold ${
                            isUnderCurrentEnergyTarget ? 'text-emerald-700' : 'text-red-600'
                          }`}
                        >
                          {isUnderCurrentEnergyTarget ? 'under' : 'exceeding'}
                        </span>{' '}
                        the current Energy target based on its energy intensity of{' '}
                        <span
                          className={`font-bold ${
                            isUnderCurrentEnergyTarget ? 'text-emerald-700' : 'text-red-600'
                          }`}
                        >
                          {formatValue(buildingEnergyValue)} kWh/m2/yr
                        </span>
                        .
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-[#2b4331]">
                      Current targets
                    </p>
                    <p className="text-[#2b4331]">
                      No targets available for {currentYear}.
                    </p>
                  </div>
                )}
                {activePoint && (
                  <div className="space-y-1 text-[13px] text-[#2b4331]">
                    <p className="text-[13px] font-semibold text-[#2b4331]">
                      Year {activePoint.year}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: ghgColor }}
                      />
                      <span className="font-medium">GHG intensity:</span>
                      <span className="text-[#1f2933]">
                        {formatValue(activePoint.ghgValue)}{' '}
                        {targets.unitGhg || 'kgCO2e/mAı/yr'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: energyColor }}
                      />
                      <span className="font-medium">Energy intensity:</span>
                      <span className="text-[#1f2933]">
                        {formatValue(activePoint.energyValue)}{' '}
                        {targets.unitEnergy || 'kWh/mAı/yr'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#4f6b5a]">
            Targets are not available in chart form for this selection.
          </div>
        )}
      </div>

      {/* table below */}
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex-1">
          <p className="mb-2 text-[13px] font-semibold tracking-wide text-[#2b4331] text-center">
            GHG intensity ({targets.unitGhg || 'kgCO2e/m²/yr'})
          </p>
          <div className="overflow-hidden rounded-lg border border-[#d5dacc]/80">
            <table className="w-full border-collapse text-[13px] text-[#2b4331]">
              <thead>
                <tr className="text-[12px] text-[#4f6b5a]">
                  {Array.from({ length: columnCount }).map((_, colIndex) => (
                    <Fragment key={`ghg-head-${colIndex}`}>
                      <th className="border-b border-[#d5dacc] px-2 py-1 text-left font-semibold">
                        Year
                      </th>
                      <th className="border-b border-[#d5dacc] px-2 py-1 text-right font-semibold">
                        Value
                      </th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ghgRows.map((row, rowIndex) => {
                  const isLastRow = rowIndex === ghgRows.length - 1;
                  const borderClass = isLastRow ? '' : 'border-b border-[#e2e8d8]';
                  return (
                    <tr key={`ghg-${rowIndex}`}>
                      {row.map((cell, colIndex) => (
                        <Fragment key={`ghg-${rowIndex}-${colIndex}`}>
                          <td className={`${borderClass} px-2 py-1 text-left text-[#4f6b5a]`}>
                            {cell?.year ?? ''}
                          </td>
                          <td className={`${borderClass} px-2 py-1 text-right font-semibold text-[#1f2933]`}>
                            {cell && cell.value != null
                              ? formatValue(cell.value)
                              : '—'}
                          </td>
                        </Fragment>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex-1">
          <p className="mb-2 text-[13px] font-semibold tracking-wide text-[#2b4331] text-center">
            Energy intensity ({targets.unitEnergy || 'kWh/m²/yr'})
          </p>
          <div className="overflow-hidden rounded-lg border border-[#d5dacc]/80">
            <table className="w-full border-collapse text-[13px] text-[#2b4331]">
              <thead>
                <tr className="text-[12px] text-[#4f6b5a]">
                  {Array.from({ length: columnCount }).map((_, colIndex) => (
                    <Fragment key={`energy-head-${colIndex}`}>
                      <th className="border-b border-[#d5dacc] px-2 py-1 text-left font-semibold">
                        Year
                      </th>
                      <th className="border-b border-[#d5dacc] px-2 py-1 text-right font-semibold">
                        Value
                      </th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {energyRows.map((row, rowIndex) => {
                  const isLastRow = rowIndex === energyRows.length - 1;
                  const borderClass = isLastRow ? '' : 'border-b border-[#e2e8d8]';
                  return (
                    <tr key={`energy-${rowIndex}`}>
                      {row.map((cell, colIndex) => (
                        <Fragment key={`energy-${rowIndex}-${colIndex}`}>
                          <td className={`${borderClass} px-2 py-1 text-left text-[#4f6b5a]`}>
                            {cell?.year ?? ''}
                          </td>
                          <td className={`${borderClass} px-2 py-1 text-right font-semibold text-[#1f2933]`}>
                            {cell && cell.value != null
                              ? formatValue(cell.value)
                              : '—'}
                          </td>
                        </Fragment>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-[#4f6b5a]">
        Data source:{' '}
        <span
          className="cursor-help underline decoration-dotted font-semibold text-[#2b4331]"
          title="CRREM Risk Assessment Tool"
        >
          crrem.org
        </span>
      </div>
    </div>
  );
}

const ProjectInfo = ({ lcaData, currentImpactData, gwpScrollContainerRef, gwpScrollIndicator, energyIntensityKwhPerM2Yr }) => {
  if (!lcaData || !currentImpactData) return null;

  const area = lcaData?.buildingInfo?.area || 1000;
  const serviceLife = lcaData?.buildingInfo?.serviceLife || 50;
  const operationalB6Total = useMemo(() => {
    const stages = lcaData?.lcaStagesData;
    if (!stages || Object.keys(stages).length === 0) return 0;
    return Object.values(stages).reduce((sum, stage) => {
      const value = Number(stage?.B6 || 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [lcaData]);

  const embodiedImpactTotal = useMemo(() => {
    const total = Number(currentImpactData.total || 0);
    if (!Number.isFinite(total)) return 0;
    const embodied = total - operationalB6Total;
    return embodied > 0 ? embodied : 0;
  }, [currentImpactData, operationalB6Total]);

  const buildingGWP = embodiedImpactTotal / area / serviceLife;
  const buildingEnergyIntensity = useMemo(() => {
    if (Number.isFinite(energyIntensityKwhPerM2Yr)) return energyIntensityKwhPerM2Yr;
    const info = lcaData?.buildingInfo || {};
    const candidates = [
      info.energyUseKwhPerM2Yr,
      info.energyIntensityKwhPerM2Yr,
      info.energyImpactKwhPerM2Yr,
      info.energyImpact,
      info.energyIntensity
    ];
    const nextValue = candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0);
    return Number.isFinite(nextValue) ? nextValue : null;
  }, [energyIntensityKwhPerM2Yr, lcaData]);
  const buildingCountry = useMemo(() => {
    const location = lcaData?.buildingInfo?.location || '';
    const parts = location.split(',').map((part) => part.trim()).filter(Boolean);
    if (!parts.length) return '';
    return parts[parts.length - 1];
  }, [lcaData?.buildingInfo?.location]);

  // Building typology hint for CRREM (update these fields if your schema differs)
  const buildingTypology =
    lcaData?.buildingInfo?.crremUseCode ||
    lcaData?.buildingInfo?.useCode ||
    lcaData?.buildingInfo?.buildingType ||
    lcaData?.buildingInfo?.typology ||
    lcaData?.buildingInfo?.useType ||
    '';

  const [crremShortTermSummary, setCrremShortTermSummary] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!buildingCountry) {
      setCrremShortTermSummary(null);
      return () => {
        cancelled = true;
      };
    }

    prepareCrremTargetsData()
      .then((data) => {
        if (cancelled) return;
        const match = findCrremTargetsForBuilding({
          data,
          countryName: buildingCountry,
          typologyHint: buildingTypology,
        });

        if (!match) {
          setCrremShortTermSummary(null);
          return;
        }

        const shortTargets = getCrremShortTermTargets(match);
        if (!shortTargets.length) {
          setCrremShortTermSummary(null);
          return;
        }

        setCrremShortTermSummary({
          country: match.country,
          countryCode: match.countryCode,
          useCode: match.useCode,
          useLabel: match.useLabel,
          unitGhg: match.unitGhg,
          unitEnergy: match.unitEnergy,
          buildingEnergyIntensity,
          targets: shortTargets,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load CRREM short term summary', err);
        setCrremShortTermSummary(null);
      });

    return () => {
      cancelled = true;
    };
  }, [buildingCountry, buildingTypology, buildingEnergyIntensity]);

  useEffect(() => {
    prepareElectricityMixData().catch((err) => {
      console.error('Prefetch electricity mix failed', err);
    });
  }, []);

  return (
    <div className="relative bg-white/80 backdrop-blur-sm border border-emerald-100 rounded-xl shadow-md px-4 pt-6 pb-10 sm:px-6 overflow-visible">
      <div className="mb-6 flex flex-col items-center gap-2 text-center sm:grid sm:grid-cols-3 sm:items-center">
        <h1 className="text-xl font-semibold text-gray-900 uppercase sm:col-start-2 sm:text-center">Project summary</h1>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 sm:col-start-3 sm:justify-self-end"
          onClick={() => setShowDetails(true)}
        >
          Detailed info
        </Button>
      </div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="text-center md:text-left">
          <div className="text-center md:text-left">
            <span className="block text-m font-semibold text-emerald-700 uppercase tracking-wide mb-2">
              GWP Impact Benchmarking
            </span>
            <span className="block text-sm text-gray-600 text-left">
              Building performance vs international targets in kg CO2-eq/m2/year (transportation impact included where required).
            </span>
          </div>
        </div>
        <div className="text-center md:text-right">
          <div className="text-2xl font-bold text-emerald-600">
            {(buildingGWP || 0).toFixed(2)}
          </div>
          <div className="text-sm text-gray-600">kg CO2-eq/m2/year</div>
        </div>
      </div>

      <div
        ref={gwpScrollContainerRef}
        className="-mx-4 overflow-x-scroll pb-6 sm:mx-0 sm:pb-6 lg:overflow-x-visible hide-scrollbar"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="w-full sm:w-auto px-4 pb-3" style={{ minWidth: '680px' }}>
          <GWPBenchmark buildingGWP={buildingGWP} />
        </div>
      </div>

      <ScrollIndicator
        indicator={gwpScrollIndicator}
        className="mx-4 mt-4 h-1 sm:mx-0"
      />

      {crremShortTermSummary && (
        <div className="mt-6 border-t border-[#e3e8dd] pt-4">
          <CrremShortTermBenchmark
            buildingGwp={buildingGWP}
            summary={crremShortTermSummary}
          />
        </div>
      )}

      {currentImpactData && (
        <div
          className={
            crremShortTermSummary
              ? 'mt-6 border-t border-[#e3e8dd] pt-4'
              : 'mt-4 sm:-mt-4'
          }
        >
          <InterestingFacts
            totalKg={currentImpactData.total || 0}
            compactTop={Boolean(crremShortTermSummary)}
          />
        </div>
      )}

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="flex w-full max-h-[92vh] max-w-[1200px] flex-col overflow-hidden rounded-3xl border border-[#d5dacc] bg-[#f3f6f1]/90 p-6 shadow-2xl">
          {showDetails && (
            <div className="max-h-[86vh] overflow-y-auto pr-2 space-y-10">
              <ElectricityMixExplorer defaultCountry={buildingCountry} />

              <div className="mt-4 border-t border-[#d5dacc]/80 pt-4">
                <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.35em] text-[#2f6046]">
                  CRREM targets
                </p>
                <CrremTargetsTable
                  countryName={buildingCountry}
                  buildingTypology={buildingTypology}
                  buildingGwp={buildingGWP}
                  buildingEnergyIntensity={buildingEnergyIntensity}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectInfo;
